import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
    Alert, PrimaryButton, Skeleton, TextInput,
} from '@luminalearning/components';

type ApiCredentials = {
    clientId: string;
    clientSecret: string;
    error: string | null;
};

type ApiCredentialsSectionProps = {
    activeCredentials: ApiCredentials;
    isCredentialsLoading: boolean;
    onGenerateApiKey: () => void;
    isGeneratingApiKey: boolean;
};

const ApiCredentialsSection = ({
    activeCredentials,
    isCredentialsLoading,
    onGenerateApiKey,
    isGeneratingApiKey,
}: ApiCredentialsSectionProps) => {
    const intl = useIntl();
    const [copiedField, setCopiedField] = useState<'clientId' | 'clientSecret' | null>(null);
    const { clientId, clientSecret } = activeCredentials;

    const handleCopyCredential = async (value: string, field: 'clientId' | 'clientSecret') => {
        try {
            if (!navigator.clipboard?.writeText) return;
            await navigator.clipboard.writeText(value);
            setCopiedField(field);
            setTimeout(() => setCopiedField(null), 1500);
        } catch {
            setCopiedField(null);
        }
    };

    return (
        <>
            <h2 className="mt-5 text-2xl font-bold">
                <FormattedMessage id="LP_DEVELOPER_API_CREDENTIALS_TITLE" defaultMessage="API Credentials" />
            </h2>
            <div className='mt-2 text-paragraph-reg'>
                <FormattedMessage
                    id="LP_DEVELOPER_API_CREDENTIALS_DESCRIPTION"
                    defaultMessage="Manage your API credentials for authenticating with the Lumina Learning API. Use the button below to generate a new set of credentials."
                />
            </div>
            <div className='mt-4 w-full xl:pr-4 2xl:pr-20 3xl:pr-2'>
                <div className='border-1.5 border-card-normal rounded-lg p-6 bg-white shadow-card'>
                    <div className='flex flex-col gap-y-4'>
                        {activeCredentials.error && (
                            <Alert
                                type='Error'
                                message={intl.formatMessage({
                                    id: 'LP_DEVELOPER_API_CREDENTIALS_ERROR',
                                    defaultMessage: 'Failed to load API credentials. Please try again.',
                                })}
                            />
                        )}
                        <div className='flex flex-col gap-y-1'>
                            <span className='text-label-reg font-bold'>
                                <FormattedMessage id="LP_DEVELOPER_CLIENT_ID_LABEL" defaultMessage="Client ID" />
                            </span>
                            <div className='flex items-center gap-x-2'>
                                <div className='relative w-full'>

                                    {isCredentialsLoading
                                        ? <div className='flex flex-col'>
                                            <Skeleton
                                                baseColor='#d7d7d7'
                                                highlightColor='#c8c8c8'
                                                height={46}
                                                width='100%'
                                            />
                                        </div>
                                        : <TextInput
                                            value={clientId}
                                            setValue={() => {}}
                                            isReadOnly
                                            placeholder={intl.formatMessage({
                                                id: 'LP_DEVELOPER_CLIENT_ID_PLACEHOLDER',
                                                defaultMessage: 'No client ID generated yet',
                                            })}
                                            className='w-full'
                                            labelText={intl.formatMessage({
                                                id: 'LP_DEVELOPER_CLIENT_ID_LABEL',
                                                defaultMessage: 'Client ID',
                                            })}
                                        />
                                    }
                                </div>
                                <button
                                    type='button'
                                    onClick={() => handleCopyCredential(clientId, 'clientId')}
                                    disabled={!clientId || isCredentialsLoading}
                                    className={[
                                        'shrink-0 rounded-full border border-card-normal px-4 py-2',
                                        'text-paragraph-sm font-semibold',
                                        clientId && !isCredentialsLoading
                                            ? [
                                                'text-text-subtle hover:text-text-default hover:border-card-strong',
                                                'transition',
                                            ].join(' ')
                                            : 'text-text-subtle opacity-50 cursor-not-allowed',
                                    ].join(' ')}
                                >
                                    {copiedField === 'clientId'
                                        ? <FormattedMessage id="LP_DEVELOPER_COPIED" defaultMessage="Copied" />
                                        : <FormattedMessage id="LP_DEVELOPER_COPY" defaultMessage="Copy" />}
                                </button>
                            </div>
                        </div>
                        <div className='flex flex-col gap-y-1'>
                            <span className='text-label-reg font-bold'>
                                <FormattedMessage id="LP_DEVELOPER_CLIENT_SECRET_LABEL" defaultMessage="Client Secret" />
                            </span>
                            <div className='flex items-center gap-x-2'>
                                <div className='relative w-full'>
                                    {isCredentialsLoading
                                        ? <div className='flex flex-col'>
                                            <Skeleton
                                                baseColor='#d7d7d7'
                                                highlightColor='#c8c8c8'
                                                height={46}
                                                width='100%'
                                            />
                                        </div>
                                        : <TextInput
                                            value={clientSecret}
                                            setValue={() => {}}
                                            isReadOnly
                                            placeholder={intl.formatMessage({
                                                id: 'LP_DEVELOPER_CLIENT_SECRET_PLACEHOLDER',
                                                defaultMessage: 'No client secret generated yet',
                                            })}
                                            className='w-full'
                                            labelText={intl.formatMessage({
                                                id: 'LP_DEVELOPER_CLIENT_SECRET_LABEL',
                                                defaultMessage: 'Client Secret',
                                            })}
                                        />
                                    }
                                </div>
                                <button
                                    type='button'
                                    onClick={() => handleCopyCredential(clientSecret, 'clientSecret')}
                                    disabled={!clientSecret || isCredentialsLoading}
                                    className={[
                                        'shrink-0 rounded-full border border-card-normal px-4 py-2',
                                        'text-paragraph-sm font-semibold',
                                        clientSecret && !isCredentialsLoading
                                            ? [
                                                'text-text-subtle hover:text-text-default hover:border-card-strong',
                                                'transition',
                                            ].join(' ')
                                            : 'text-text-subtle opacity-50 cursor-not-allowed',
                                    ].join(' ')}
                                >
                                    {copiedField === 'clientSecret'
                                        ? <FormattedMessage id="LP_DEVELOPER_COPIED" defaultMessage="Copied" />
                                        : <FormattedMessage id="LP_DEVELOPER_COPY" defaultMessage="Copy" />}
                                </button>
                            </div>
                        </div>
                        <div className='flex justify-end'>
                            <PrimaryButton
                                type='button'
                                text={intl.formatMessage({
                                    id: 'LP_DEVELOPER_GENERATE_CREDENTIALS',
                                    defaultMessage: 'Generate new credentials',
                                })}
                                onClick={onGenerateApiKey}
                                isLoading={isGeneratingApiKey}
                                isDisabled={isGeneratingApiKey}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ApiCredentialsSection;
